import { Device } from "@twilio/voice-sdk";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import _ from "lodash";
import { action, computed, decorate, observable, observe } from "mobx";
import moment from "moment";
import "moment-duration-format";
import TwilioService from "./utils/TwilioService";

class DialerStore {
  incomingConnections = []; // [{ CallSid, call, ... }]
  userStatus = "Available";
  device = null;
  showDialer = false;
  showOptions = false;
  showOnHold = false;
  dialerNumber = "";
  currentCallDuration = null;
  suggestedNumbers = [];
  callerID = null;
  onHoldCalls = [];
  confirmationCall = false;
  pendingOutcomeCallRecordID = null;
  showOutcomeModal = false;
  pendingOutcomeIsIncoming = false;

  // Internals
  initialised = false;
  dialerStatus = null; // "readyToDial" | "incoming" | "busy" | "options" | "on_hold" | "offline" | device.state
  currentCall = null; // { call, ... }
  currentCallSID = null;
  updateDeviceStatusTimeout = null;
  updateUserStatusTimeout = null;
  updateCallDurationTimeout = null;
  fetchOnHoldCallsTimeout = null;
  closeAfterReject = false;

  constructor() {
    this.debouncedFetchNumberSuggestions = _.debounce(
      () => this.fetchNumberSuggestions(),
      500
    );

    observe(this, "userStatus", () => {
      this.updateUserStatus();
    });

    observe(this, "dialerNumber", change => {
      if (this.callerID && this.callerID.number !== change.newValue)
        this.callerID = null;
      this.debouncedFetchNumberSuggestions();
    });
  }

  init = async () => {
    const token = await TwilioService.accessToken();

    // v2: create Device with token + options; logLevel replaces { debug: true }
    this.device = new Device(token, { logLevel: "debug" });

    // Register to receive incoming calls
    this.device.register();

    // DEVICE-LEVEL EVENTS (v2)
    this.device.on("registered", () => {
      console.log("Twilio.Device registered (ready for incoming).");
      // Only set ready if not already busy/incoming/options/hold
      if (
        !this.currentCall &&
        !this.showOptions &&
        !this.showOnHold &&
        this.incomingConnections.length === 0
      ) {
        this.dialerStatus = "readyToDial";
      }
    });

    this.device.on("registering", () => {
      console.log("Twilio.Device registering...");
    });

    this.device.on("unregistered", () => {
      console.log("Twilio.Device unregistered.");
      this.dialerStatus = "offline";
    });

    this.device.on("error", error => {
      console.log("Twilio.Device Error:", error);
    });

    // Incoming call
    this.device.on("incoming", call => {
      console.log("Incoming call");
      if (this.userStatus === "Available") {
        this.closeAfterReject = this.showDialer === false;

        const paramsString = _.get(call, "parameters.Params", "");
        const urlParams = new URLSearchParams(paramsString);

        // Parse values
        const CallSid = _.get(call, "parameters.CallSid");
        const callRecordID = urlParams.get("call_record_id");
        const callRecordContactID = urlParams.get("call_record_contact_id");
        const callRecordName =
          urlParams.get("call_record_contact_email") || "<unknown caller>";

        this.incomingConnections.push({
          CallSid,
          call,
          callRecordID,
          callRecordName,
          callRecordContactID
        });

        this.showDialer = true;
        this.dialerStatus = "incoming";

        call.on("accept", () => {
          console.log("Call accepted!", call);
          this.currentCallSID = _.get(call, "parameters.CallSid");
          this.dialerStatus = "busy";
          this.updateCallDuration();
        });

        call.on("disconnect", () => {
          console.log("Call ended.");
          // Store the call record ID for outcome modal (for incoming calls)
          const callRecordID = this.currentCall ? this.currentCall.callRecordID : null;
          
          this.currentCall = null;
          this.currentCallSID = null;
          this.callerID = null;
          this.dialerStatus = "readyToDial";
          clearTimeout(this.updateCallDurationTimeout);
          
          // Show outcome modal for incoming calls
          if (callRecordID) {
            this.pendingOutcomeCallRecordID = callRecordID;
            this.pendingOutcomeIsIncoming = true;
            this.showOutcomeModal = true;
          }
        });

        call.on("cancel", () => {
          console.log("Call cancelled.");
          this.removeIncomingConnection(CallSid);
          this.currentCall = null;
          // If there are still other incoming, remain 'incoming', else ready
          this.dialerStatus =
            this.incomingConnections.length > 0 ? "incoming" : "readyToDial";
          if (this.closeAfterReject) {
            this.closeAfterReject = false;
            this.showDialer = false;
          }
        });
      } else {
        // Not available
        call.reject();
      }
    });

    // Token auto-refresh (best practice in v2)
    this.device.on("tokenWillExpire", async () => {
      const newToken = await TwilioService.accessToken();
      if (this.device) {
        this.device.updateToken(newToken);
      }
    });

    this.updateDeviceStatus();
    this.updateUserStatus();
    this.fetchOnHoldCalls();
    this.initialised = true;
  };

  get systemStatus() {
    return this.dialerStatus === "offline" ? "Offline" : this.userStatus;
  }

  removeIncomingConnection = CallSid => {
    this.incomingConnections = this.incomingConnections.filter(
      incoming => incoming.CallSid !== CallSid
    );
  };

  acceptIncoming = eventConnection => {
    const incoming = this.incomingConnections.find(
      incoming => incoming.CallSid === eventConnection.CallSid
    );
    if (!incoming) return;

    this.removeIncomingConnection(incoming.CallSid);
    const call = incoming.call;

    call.accept(); // v2 accept

    this.currentCall = {
      call,
      callRecordID: incoming.callRecordID,
      callRecordName: incoming.callRecordName,
      callRecordContactID: incoming.callRecordContactID,
      started: moment(),
      isOutgoing: false
    };

    // Move UI to busy immediately (even before 'accept' event fires back)
    this.dialerStatus = "busy";

    TwilioService.acceptedCall({
      call_record_id: incoming.callRecordID,
      call_sid: eventConnection.CallSid
    }).then(response => {
      this.currentCallSID = eventConnection.CallSid;
      this.callerID = response;
    });
  };

  rejectAllIncoming = () => {
    this.incomingConnections.forEach(eventConnection => {
      this.rejectIncoming(eventConnection);
    });
  };

  rejectIncoming = eventConnection => {
    const incoming = this.incomingConnections.find(
      incoming => incoming.CallSid === eventConnection.CallSid
    );
    if (!incoming) return;
    this.removeIncomingConnection(incoming.CallSid);
    incoming.call.reject();
    // If no more incoming, return to ready (unless busy)
    if (!this.currentCall) {
      this.dialerStatus =
        this.incomingConnections.length > 0 ? "incoming" : "readyToDial";
    }
    if (this.closeAfterReject) {
      this.closeAfterReject = false;
      this.showDialer = false;
    }
  };

  addNumberToCall = number => {
    this.hideOptions();
    this.dialerNumber = number;
    this.showDialer = true;
  };

  dial = async () => {
    if (!this.device) return;
    const phoneNumber = parsePhoneNumberFromString(this.dialerNumber, "AU");
    if (!phoneNumber) {
      console.log(
        `Error parsing phone number, dialer attempted to dial ${this.dialerNumber}`
      );
      return;
    }

    const params = {
      To: phoneNumber.number,
      confirmationCall: this.confirmationCall
    };
    // Create a call record first (keeps your old flow)
    const response = await TwilioService.createCallRecord(params);
    params.call_record_id = response.id;

    // Reset confirmation call flag after using it
    this.confirmationCall = false;

    // v2: await device.connect and keep the Call instance
    const call = await this.device.connect({ params });

    this.currentCall = {
      call,
      callRecordID: response.id,
      callRecordName: response.contact_email,
      started: moment(),
      isOutgoing: true
    };

    // Optionally show dialing as busy right away
    this.dialerStatus = "busy";

    // call events
    call.on("accept", () => {
      this.currentCallSID = _.get(call, "parameters.CallSid");
      this.dialerStatus = "busy";
      this.updateCallDuration();
    });

    call.on("disconnect", () => {
      console.log("Call ended.");
      // Store the call record ID for outcome modal (only for outgoing calls)
      const callRecordID =
        this.currentCall && this.currentCall.isOutgoing
          ? this.currentCall.callRecordID
          : null;

      this.currentCall = null;
      this.currentCallSID = null;
      this.callerID = null;
      this.dialerStatus = "readyToDial";
      clearTimeout(this.updateCallDurationTimeout);

      // Show outcome modal for outgoing calls
      if (callRecordID) {
        this.pendingOutcomeCallRecordID = callRecordID;
        this.pendingOutcomeIsIncoming = false;
        this.showOutcomeModal = true;
      }
    });

    call.on("cancel", () => {
      console.log("Cancelled.");
      this.currentCall = null;
      // If cancelled before connected, go back to ready
      this.dialerStatus = "readyToDial";
    });

    // delayed update once CallSid is known
    setTimeout(() => {
      params.call_sid = this.currentCallSID;
      TwilioService.updateCallRecord(params);
    }, 2000);
  };

  // Queues / hold features: connect to queue by dialing To=queue_name
  pickUpHold = async queue_name => {
    if (!this.device) return;
    this.hideOnHold();

    const params = { To: queue_name };
    const response = await TwilioService.createCallRecord(params);
    params.call_record_id = response.id;

    const call = await this.device.connect({ params });

    this.currentCall = {
      call,
      callRecordID: response.id,
      callRecordName: response.contact_email,
      started: moment(),
      isOutgoing: true
    };

    // Show busy immediately
    this.dialerStatus = "busy";
  };

  putCallOnHold = () => {
    if (!_.get(this.currentCall, "call")) {
      console.log("No Call");
      return;
    }
    // server-side queueing
    TwilioService.pushCallToQueue({
      call_record_id: this.currentCall.callRecordID,
      queue: "hold"
    }).then(() => { });
  };

  closeDialer = () => {
    if (this.incomingConnections.length > 0) this.rejectAllIncoming();
    this.hideOptions();
    this.showDialer = false;
  };

  displayOnHold = () => {
    this.showOnHold = true;
    this.updateDeviceStatus();
  };

  hideOnHold = () => {
    this.showOnHold = false;
    this.updateDeviceStatus();
  };

  displayOptions = () => {
    this.showOptions = true;
    this.updateDeviceStatus();
  };

  hideOptions = () => {
    this.showOptions = false;
    this.updateDeviceStatus();
  };

  isValidDialNumber = () => {
    if (!this.dialerNumber || this.dialerNumber === "") return false;
    const phoneNumber = parsePhoneNumberFromString(this.dialerNumber, "AU");
    if (!phoneNumber) return false;
    return phoneNumber.isValid();
  };

  disconnect = async () => {
    const callRecordID = this.currentCall ? this.currentCall.callRecordID : null;
    const isOutgoing = this.currentCall ? this.currentCall.isOutgoing : false;

    if (this.device) {
      this.device.disconnectAll();
    }

    this.currentCall = null;
    this.currentCallSID = null;
    this.callerID = null;
    this.dialerStatus = "readyToDial";

    if (callRecordID) {
      this.pendingOutcomeCallRecordID = callRecordID;
      this.pendingOutcomeIsIncoming = !isOutgoing;
      this.showOutcomeModal = true;
    }
  };

  updateDeviceStatus = () => {
    clearTimeout(this.updateDeviceStatusTimeout);

    if (this.currentCall && this.currentCall.call) {
      this.dialerStatus = "busy";
      this.updateDeviceStatusTimeout = setTimeout(this.updateDeviceStatus, 200);
      return;
    }

    if (!this.device) {
      this.dialerStatus = "offline";
      return;
    }

    if (this.showOptions) {
      this.dialerStatus = "options";
    } else if (this.showOnHold) {
      this.dialerStatus = "on_hold";
    } else if (this.incomingConnections.length > 0) {
      this.dialerStatus = "incoming";
    } else {
      const state = this.device.state; // "unregistered" | "registering" | "registered" | "destroyed"
      this.dialerStatus =
        state === "registered" ? "readyToDial" : state || "offline";
    }

    this.updateDeviceStatusTimeout = setTimeout(this.updateDeviceStatus, 200);
  };

  updateUserStatus = () => {
    clearTimeout(this.updateUserStatusTimeout);
    TwilioService.updateUserStatus(this.userStatus);
    this.updateUserStatusTimeout = setTimeout(this.updateUserStatus, 60000);
  };

  updateCallDuration = () => {
    clearTimeout(this.updateCallDurationTimeout);
    if (!_.get(this.currentCall, "started")) return;
    const date = new Date(null);
    const seconds = moment(new Date()).diff(
      this.currentCall.started,
      "seconds"
    );
    date.setSeconds(seconds);
    this.currentCallDuration = date.toISOString().substr(11, 8);
    this.updateCallDurationTimeout = setTimeout(this.updateCallDuration, 500);
  };

  fetchOnHoldCalls = () => {
    clearTimeout(this.fetchOnHoldCallsTimeout);
    TwilioService.fetchOnHoldCalls().then(response => {
      this.onHoldCalls = response;
      this.fetchOnHoldCallsTimeout = setTimeout(this.fetchOnHoldCalls, 10000);
    });
  };

  fetchNumberSuggestions = () => {
    if (!this.dialerNumber || this.dialerNumber === "")
      return (this.suggestedNumbers = []);
    TwilioService.fetchDialerSuggestions(this.dialerNumber).then(response => {
      if (response.length === 1 && response[0].number === this.dialerNumber) {
        this.suggestedNumbers = [];
        TwilioService.fetchCallerID(response[0].id).then(response => {
          this.callerID = response;
        });
      } else {
        this.suggestedNumbers = response;
      }
    });
  };

  submitCallOutcome = (outcome, isTCSCall) => {
    if (this.pendingOutcomeCallRecordID) {
      const params = {
        call_record_id: this.pendingOutcomeCallRecordID
      };
      
      // For incoming calls, only send the TCS flag
      if (this.pendingOutcomeIsIncoming) {
        params.confirmation_call = isTCSCall;
      } else {
        // For outgoing calls, only send the outcome
        params.outcome = outcome;
      }
      
      TwilioService.updateCallOutcome(params)
        .then(response => {
          console.log("Call outcome submitted:", params, response);
          this.showOutcomeModal = false;
          this.pendingOutcomeCallRecordID = null;
          this.pendingOutcomeIsIncoming = false;
        })
        .catch(error => {
          console.error("Error submitting call outcome:", error);
          // Still close the modal even if there's an error
          this.showOutcomeModal = false;
          this.pendingOutcomeCallRecordID = null;
          this.pendingOutcomeIsIncoming = false;
        });
    }
  };
}

export default decorate(DialerStore, {
  initialised: observable,
  userStatus: observable,
  dialerStatus: observable,
  onHoldCalls: observable,
  currentCall: observable,
  currentCallSID: observable,
  incomingConnections: observable,
  showDialer: observable,
  dialerNumber: observable,
  suggestedNumbers: observable,
  callerID: observable,
  showOnHold: observable,
  displayOnHold: action,
  showOptions: observable,
  displayOptions: action,
  hideOptions: action,
  closeDialer: action,
  updateUserStatus: action,
  updateCallDuration: action,
  currentCallDuration: observable,
  systemStatus: computed,
  showOutcomeModal: observable,
  pendingOutcomeCallRecordID: observable,
  pendingOutcomeIsIncoming: observable,
  submitCallOutcome: action
});
